import { isEmpty, isUndefined } from 'lodash-es';
import { dollarsToCents } from 'utils';

import { ICartEntry, IDiscount } from '@rbi-ctg/menu';
import { DiscountTypes } from 'enums/menu';
import { IAppliedRewards } from 'state/loyalty/hooks/types';
import { ISelectedOfferPersistedState } from 'state/offers/hooks/types';
import { priceForCartEntry } from 'utils/menu/price';

interface ICartEntryForPricing extends Pick<ICartEntry, 'price' | 'quantity' | 'cartId'> {}

interface CartTotalInput {
  offerDetails: ISelectedOfferPersistedState;
  cartEntries: ICartEntry[];
  loyaltyInfo: { loyaltyEnabled: boolean; appliedLoyaltyRewards?: IAppliedRewards };
}

export const getIndividualItemPrice = ({
  cartEntry,
}: {
  cartEntry: ICartEntryForPricing;
}): number => (cartEntry.price || 0) / cartEntry.quantity;

export const getItemLoyaltyRewardDiscount = ({
  cartEntry,
  appliedLoyaltyRewards,
}: {
  cartEntry: ICartEntryForPricing;
  appliedLoyaltyRewards: IAppliedRewards;
}) => {
  const individualItemPrice = getIndividualItemPrice({ cartEntry });
  const timesRewardApplied = appliedLoyaltyRewards[cartEntry.cartId]?.timesApplied ?? 0;
  return individualItemPrice * timesRewardApplied;
};

export const getLoyaltyRewardsDiscount = ({
  cartEntries,
  loyaltyEnabled,
  appliedLoyaltyRewards,
}: {
  cartEntries: ICartEntryForPricing[];
  loyaltyEnabled: boolean;
  appliedLoyaltyRewards?: IAppliedRewards;
}) =>
  !loyaltyEnabled || isUndefined(appliedLoyaltyRewards) || isEmpty(appliedLoyaltyRewards)
    ? 0
    : cartEntries.reduce((totalRewardsDiscount: number, cartEntry: ICartEntry) => {
        const discount = getItemLoyaltyRewardDiscount({ cartEntry, appliedLoyaltyRewards });
        return totalRewardsDiscount + discount;
      }, 0);

export const computeCartTotal = (
  offerDetails: ISelectedOfferPersistedState,
  cartEntries: ICartEntry[],
  loyaltyInfo: { loyaltyEnabled: boolean; appliedLoyaltyRewards?: IAppliedRewards }
) => {
  const { selectedOffer, selectedOfferCartEntry, selectedOfferPrice } = offerDetails;
  const discountPrice = -Math.abs(dollarsToCents(selectedOffer?.option?.discountValue));

  const cartEntriesWithOffer = selectedOffer
    ? [
        ...cartEntries,
        {
          ...selectedOfferCartEntry,
          price: selectedOfferPrice || discountPrice || 0,
        },
      ]
    : cartEntries;

  const loyaltyRewardsDiscount = getLoyaltyRewardsDiscount({
    cartEntries,
    ...loyaltyInfo,
  });

  return (
    Math.max(
      cartEntriesWithOffer.reduce(
        (totalPrice: number, entry: ICartEntry) => priceForCartEntry(entry) + totalPrice,
        0
      ),
      0
    ) - loyaltyRewardsDiscount
  );
};

export const computeOtherDiscountAmount = (allDiscounts?: IDiscount[]) => {
  if (!allDiscounts) {
    return 0;
  }
  return allDiscounts
    .filter(discount => discount.name !== DiscountTypes.REWARDS_DISCOUNTS)
    .reduce((acc, curr) => curr.value + acc, 0);
};

export const computeDeliveryFee = ({
  feeCents,
  feeDiscountCents,
  serviceFeeCents,
  smallCartFeeCents,
}: {
  feeCents: number | null;
  feeDiscountCents: number | null;
  serviceFeeCents: number | null;
  smallCartFeeCents: number | null;
}) => {
  // The value "feeCents" includes the additional fees "serviceFeeCents" and "smallCartFeeCents"
  return Math.max(
    (feeCents || 0) - (feeDiscountCents || 0) - (serviceFeeCents || 0) - (smallCartFeeCents || 0),
    0
  );
};
